import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useParserStatistics, useParserStatisticsSummary } from '@/hooks/useParserStatistics';
import { useAPILogs } from '@/hooks/useAPILogs';
import { Activity, CheckCircle, XCircle, Clock, Zap, Shield, TrendingUp } from 'lucide-react';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { format } from 'date-fns';

export const ParserStatisticsDashboard = () => {
  const summary = useParserStatisticsSummary(30);
  const statistics = useParserStatistics();
  const recentLogs = useAPILogs(20);

  if (summary.isLoading || statistics.isLoading || recentLogs.isLoading) {
    return <div className="p-6">Loading statistics...</div>;
  }

  const summaryData = summary.data;
  const stats = statistics.data || [];
  const logs = recentLogs.data || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {summaryData?.successRate || 0}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Last 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Parsed</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summaryData?.totalParsed || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {summaryData?.totalFailed || 0} failed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Confidence</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {summaryData?.avgConfidence || 0}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Overall quality score
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Processing</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summaryData?.avgProcessingTime || 0}ms
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Per bill
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Provider Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Provider Breakdown</CardTitle>
          <CardDescription>
            Distribution of parsing providers over the last 30 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="default">BillPortal API</Badge>
                <span className="text-sm text-muted-foreground">Primary parser</span>
              </div>
              <span className="text-2xl font-bold">{summaryData?.billportalParses || 0}</span>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Badge variant="secondary">OpenAI Fallback</Badge>
                <span className="text-sm text-muted-foreground">Backup parser</span>
              </div>
              <span className="text-2xl font-bold">{summaryData?.openaiParses || 0}</span>
            </div>

            {summaryData && summaryData.authFailures > 0 && (
              <div className="flex items-center justify-between text-orange-600">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  <span className="text-sm font-medium">Auth Failures</span>
                </div>
                <span className="text-2xl font-bold">{summaryData.authFailures}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs for detailed views */}
      <Tabs defaultValue="logs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="logs">Recent API Logs</TabsTrigger>
          <TabsTrigger value="trends">Daily Trends</TabsTrigger>
        </TabsList>

        <TabsContent value="logs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent API Calls</CardTitle>
              <CardDescription>Last 20 API interactions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {logs.map((log) => (
                  <div 
                    key={log.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {log.was_successful ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                      <div>
                        <div className="font-medium text-sm">
                          {log.http_method} {log.api_endpoint}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <span>{format(new Date(log.created_at), 'MMM dd, HH:mm:ss')}</span>
                          <Badge variant="outline" className="text-xs">
                            {log.http_status_code}
                          </Badge>
                          {log.used_fallback && (
                            <Badge variant="secondary" className="text-xs">
                              Fallback: {log.fallback_provider}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium">{log.processing_duration_ms}ms</div>
                      {log.auth_status && (
                        <Badge 
                          variant={log.auth_status === 'success' ? 'default' : log.auth_status === 'failed' ? 'destructive' : 'secondary'}
                          className="text-xs mt-1"
                        >
                          {log.auth_status}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Daily Statistics</CardTitle>
              <CardDescription>Parser performance over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {stats.slice(0, 10).map((stat) => (
                  <div 
                    key={stat.date}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="font-medium">{format(new Date(stat.date), 'MMM dd, yyyy')}</div>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-green-600">
                        <CheckCircle className="h-4 w-4 inline mr-1" />
                        {stat.total_bills_parsed}
                      </div>
                      <div className="text-red-600">
                        <XCircle className="h-4 w-4 inline mr-1" />
                        {stat.total_bills_failed}
                      </div>
                      <div className="text-muted-foreground">
                        {stat.avg_confidence_score?.toFixed(1) || 0}% confidence
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
